import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';
import { Action, ActionReducer, createReducer, on } from '@ngrx/store';

import { Task } from '@lyceo/shared/plan/util-models';
import { CallState, LoadingState } from '@lyceo/shared/util-loading';
import { PlanPageActions } from '../plan/plan.actions';
import { PupilContextActions } from '@lyceo/shared/pupil-context/data-access';
import {
  ReflectionModalActions,
  TaskActions,
  TaskModalActions,
  TaskPageActions,
} from './task.actions';
import { getParentTaskId } from '@lyceo/shared/plan/util';
import { AuthActions } from '@lyceo/shared/auth/data-access';

export const TASK_FEATURE_KEY: string = 'task';

export interface TaskState extends EntityState<Task> {
  callState: CallState;
  listCallState: CallState;
  todoCallState: CallState;
  taskModalSubtaskCallState: CallState;
  moveCallState: CallState;
  selectedId: string | undefined;
  todoIds: string[];
  reflectionId?: string;
}

export const taskAdapter: EntityAdapter<Task> = createEntityAdapter<Task>();

export const initialTaskState: TaskState = taskAdapter.getInitialState({
  callState: LoadingState.INIT,
  listCallState: LoadingState.INIT,
  todoCallState: LoadingState.INIT,
  taskModalSubtaskCallState: LoadingState.INIT,
  moveCallState: LoadingState.INIT,
  selectedId: undefined,
  todoIds: [],
  reflectionId: undefined,
});

const reducer: ActionReducer<TaskState, Action> = createReducer(
  initialTaskState,
  on(
    AuthActions.logoutSuccess,
    PupilContextActions.setPupilContext,
    (): TaskState => initialTaskState,
  ),
  on(
    PupilContextActions.setPupilContext,
    PlanPageActions.calendarDayClicked,
    (state): TaskState => ({
      ...state,
      listCallState: LoadingState.LOADING,
    }),
  ),
  on(
    TaskActions.fetchByDateSuccess,
    (state, action): TaskState =>
      taskAdapter.upsertMany(action.tasks, {
        ...state,
        listCallState: LoadingState.LOADED,
      }),
  ),
  on(
    TaskActions.fetchByDateFailure,
    (state, { error }): TaskState => ({
      ...state,
      listCallState: { errorMsg: error.message },
    }),
  ),
  on(
    PupilContextActions.setPupilContext,
    TaskPageActions.init,
    (state): TaskState => ({
      ...state,
      todoCallState: LoadingState.LOADING,
    }),
  ),
  on(
    TaskActions.getTodoTasksSuccess,
    (state, action): TaskState =>
      taskAdapter.upsertMany(action.tasks, {
        ...state,
        todoIds: action.tasks.map((task) => task.id),
        todoCallState: LoadingState.LOADED,
      }),
  ),
  on(
    TaskActions.getTodoTasksFailure,
    (state, { error }): TaskState => ({
      ...state,
      todoCallState: { errorMsg: error.message },
    }),
  ),
  on(
    PupilContextActions.setPupilContext,
    TaskActions.create,
    TaskActions.update,
    ReflectionModalActions.updateReflection,
    PlanPageActions.taskMoved,
    (state): TaskState => ({
      ...state,
      callState: LoadingState.LOADING,
    }),
  ),
  on(
    TaskActions.fetchCalendarSuccess,
    TaskActions.prefetchCalendarPreviousSuccess,
    TaskActions.prefetchCalendarNextSuccess,
    (state, action): TaskState =>
      taskAdapter.upsertMany(action.tasks, {
        ...state,
        callState: LoadingState.LOADED,
      }),
  ),
  on(
    TaskActions.fetchCalendarFailure,
    TaskActions.createFailure,
    TaskActions.updateFailure,
    TaskActions.getFailure,
    ReflectionModalActions.updateReflectionFailure,
    ReflectionModalActions.upsertNoteFailure,
    (state, { error }): TaskState => ({
      ...state,
      callState: { errorMsg: error.message },
    }),
  ),
  on(
    TaskActions.deleteSuccess,
    (state, action): TaskState =>
      taskAdapter.removeMany(
        (entity) =>
          entity.parentTaskId === `/api/tasks/${action.id}` ||
          entity.id === action.id,
        state,
      ),
  ),
  on(
    TaskActions.updateSuccess,
    (state, action): TaskState =>
      taskAdapter.removeMany(
        (entity) => entity.parentTaskId === `/api/tasks/${action.task.id}`,
        state,
      ),
  ),
  on(
    TaskActions.createSuccess,
    TaskActions.updateSuccess,
    TaskActions.getSuccess,
    ReflectionModalActions.updateReflectionSuccess,
    (state, action): TaskState =>
      taskAdapter.upsertMany([action.task, ...action.task.subTasks], {
        ...state,
        callState: LoadingState.LOADED,
      }),
  ),
  on(
    TaskActions.updatePupilDoneSuccess,
    TaskActions.updateDoneAtSuccess,
    (state, action): TaskState => taskAdapter.setOne(action.task, state),
  ),
  on(
    ReflectionModalActions.updateReflection,
    ReflectionModalActions.modalClosed,
    (state): TaskState => ({
      ...state,
      reflectionId: undefined,
    }),
  ),
  on(
    TaskModalActions.updateClosed,
    (state): TaskState => ({
      ...state,
      selectedId: undefined,
      taskModalSubtaskCallState: LoadingState.INIT,
    }),
  ),
  on(
    PlanPageActions.dayListTaskClicked,
    PlanPageActions.todoTaskClicked,
    PlanPageActions.calendarTaskClicked,
    TaskPageActions.todoTaskClicked,
    (state, { task }): TaskState => {
      const parentOrTaskId: string = getParentTaskId(task);
      return {
        ...state,
        selectedId: parentOrTaskId,
        taskModalSubtaskCallState: LoadingState.LOADING,
      };
    },
  ),
  on(
    TaskActions.getSuccess,
    (state): TaskState => ({
      ...state,
      taskModalSubtaskCallState: LoadingState.LOADED,
    }),
  ),
  on(
    TaskActions.getFailure,
    (state, { error }): TaskState => ({
      ...state,
      taskModalSubtaskCallState: { errorMsg: error.message },
    }),
  ),
  on(
    PlanPageActions.pupilCompleteTaskClicked,
    (state, { task, value, showReflection }): TaskState => {
      if (task.parentTaskId && value && showReflection) {
        return {
          ...state,
          reflectionId: task.id,
        };
      }
      return state;
    },
  ),
  on(
    TaskActions.taskMovedSuccess,
    (state, { tasks }): TaskState =>
      taskAdapter.updateMany(
        tasks.map((updated) => {
          return {
            id: updated.id,
            changes: updated,
          };
        }),
        {
          ...state,
          moveCallState: LoadingState.LOADED,
        },
      ),
  ),
  on(
    PlanPageActions.taskMoved,
    (state): TaskState => ({
      ...state,
      moveCallState: LoadingState.LOADING,
    }),
  ),
  on(
    TaskActions.taskMovedFailure,
    (state, { error }): TaskState => ({
      ...state,
      moveCallState: { errorMsg: error.message },
    }),
  ),
);

export function taskReducer(
  state: TaskState | undefined,
  action: Action,
): TaskState {
  return reducer(state, action);
}
